import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// Connects to data-controller="ui--dashboard-ab-banner"
//
// Drives the whole dashboard A/B banner (CONEK-5583):
//   - switchBack(): opts the user out of v2 (cookie + localStorage + PATCH the
//     durable jsonb), resets the dismiss cookie so the banner shows again next
//     time, then Turbo-visits the React (v1) page.
//   - onDismissed(): the user closed the banner with the Alert's X. Persist the
//     dismissal in a cookie (readable server-side, so the next /v2/dashboard
//     load skips rendering the banner entirely — no flash, no empty wrapper) and
//     remove the WHOLE banner container here so no gap is left in this view.
//
// No connect()/hide on purpose: the server already decides whether to render the
// banner (from the dismiss cookie). Hiding client-side would reintroduce the
// flash this design removes. Dismissal is per-browser and only silences the
// informational banner — it does NOT change the v1/v2 preference.
export default class extends Controller {
  static values = {
    updateUrl: String,
    v1Path: String,
    pageKey: String,
    cookieName: String,
    // Dismiss cookie name — provided by the component (sourced from
    // UiVersionResolver), NOT recomputed here, so server/component/JS agree.
    dismissCookie: String,
  }

  onDismissed() {
    this.setDismissCookie()

    // Remove the whole container (this.element), not just the inner alert, so no
    // empty wrapper is left creating layout gaps. Fade it out first.
    this.element.style.transition = "opacity 200ms ease-out"
    this.element.style.opacity = "0"
    setTimeout(() => this.element.remove(), 200)
  }

  async switchBack(event) {
    event.preventDefault()

    // Fast-path client mirrors first, so a failed/slow request still leaves the
    // browser opted out on the next navigation.
    this.writeCookie(this.cookieNameValue, "v1")
    this.writeLocalStorage(`${this.pageKeyValue}_ui_pref`, "v1")
    // Leaving v2 resets the banner dismissal, so it reappears when the user opts
    // back into v2.
    this.clearDismissCookie()

    try {
      // FormData (multipart), NOT a JSON body: Rodauth's :json/:jwt features
      // authenticate a json_request? by JWT bearer, not the session cookie, so a
      // JSON body 401s. @rails/request.js sends non-FormData bodies (incl.
      // URLSearchParams) as application/json, so FormData is what keeps cookie
      // auth here (mirrors AppearanceTab).
      const body = new FormData()
      body.append("page_key", this.pageKeyValue)
      body.append("version", "v1")
      await patch(this.updateUrlValue, { body })
    } catch (_error) {
      // The cookie already opts the user out; the durable jsonb reconciles on a
      // later successful write. Navigate regardless rather than trap the user.
    }

    window.Turbo.visit(this.v1PathValue, { action: "replace" })
  }

  setDismissCookie() {
    const oneYear = 60 * 60 * 24 * 365
    document.cookie = `${this.dismissCookieValue}=1; path=/; max-age=${oneYear}; SameSite=Lax`
  }

  clearDismissCookie() {
    document.cookie = `${this.dismissCookieValue}=; path=/; max-age=0; SameSite=Lax`
  }

  writeLocalStorage(key, value) {
    try {
      window.localStorage.setItem(key, value)
    } catch (_error) {
      // Private mode / storage disabled — cookie + jsonb still carry the choice.
    }
  }

  writeCookie(name, value) {
    const oneYear = 60 * 60 * 24 * 365
    document.cookie = `${name}=${value}; path=/; max-age=${oneYear}; SameSite=Lax`
  }
}
